<template>
  <div :id="getId + '-wrapper'" class="heavy-last-grid-item">
    <div class="flex-row">
      <short-text-filter
        :id="getId + '-filter'"
        styled-label="Name"
        class="raft-js-search-add-label"
        :update-method="updateSearch"
        :use-json-api-filter-manager="false"
        ref="biomaterial_search_input"
        placeholder="Enter search terms"
        help-text="Enter at least two characters"
      />
    </div>
    <div
      id="raft-bio-search-add"
      class="d-block text-center capped-search-results-height"
    >
      <div v-if="response && totalMaterials === 0">
        No biological materials were found.
      </div>
      <b-form id="raft-bio-mat-search-add-from-library">
        <b-radio-group>
          <div
            v-if="isReady"
            id="raft-bio-search-add-results"
            class="raft-bio-mat-search-results-wrapper"
          >
            <div
              :key="bioMat.id"
              v-for="(bioMat, index) in bioMaterials"
              class="raft-bio-mat-search-result"
              :class="{ 'raft-bio-mat-odd': index % 2 }"
              @click="selectedBioMaterial = bioMat"
            >
              <b-form-radio
                v-model="selectedBioMaterial"
                :id="
                  'search-result-radio-' + bioMat.drupal_internal__id.toString()
                "
                :value="bioMat"
                class="raft-bio-mat-radio"
              >
                <span class="sr-only">
                  Select {{ bioMat.name }} with ID
                  {{ bioMat.drupal_internal__id }}
                </span>
              </b-form-radio>
              <div class="raft-bio-mat-search-result-data">
                <BioMaterialRender
                  :bio-material="bioMat"
                  :display-field-data="displayData"
                />
              </div>
            </div>
            <div>
              <b-pagination
                v-if="totalMaterials > resultsPerPage"
                @change="updateCurrentPage"
                :total-rows="totalMaterials"
                :per-page="resultsPerPage"
                limit="10"
                first-text="« first"
                prev-text="‹ previous"
                next-text="next ›"
                last-text="last »"
                align="center"
              />
            </div>
          </div>
        </b-radio-group>
      </b-form>
    </div>
    <div
      data-testid="copyFromLibrary"
      class="raft-bio-mat-search-result-actions"
    >
      <div class="raft-bio-mat-footer-left">
        <div class="raft-bio-mat-search-result-add-new">
          <h3>No matches?</h3>
          <a href="" v-if="!isBusy" @click.prevent="addMaterialStub">
            Enter new {{ lowerCasedLabel }} manually
          </a>
        </div>
        <a
          v-if="showBackButton"
          href=""
          @click.prevent="backToMaterialTypeSelect"
          data-testid="back"
          >Back</a
        >
      </div>
      <VButton
        v-if="!isBusy"
        :class="buttonStyle"
        @click="addToLibrarySelected(selectedBioMaterial, true)"
        >Select from Library</VButton
      >
      <raft-busy v-if="isBusy" busy-text="Adding to group" />
    </div>
  </div>
</template>

<script>
import debounce from "lodash/debounce";
import { JsonApiFilterManagerFactory } from "@/lib/JsonApiFilterManager";
import { jsonClient, restClient } from "@/lib/raftApi";
import RaftBusy from "@/components/RaftBusy.vue";
import ShortTextFilter from "@/components/filters/ShortTextFilter.vue";
import BioMaterialRender from "@/components/biologicals/BioMaterialRender.vue";
import raftGenericHelper from "../../lib/raft_generic_helper";
import VButton from "../raft/VButton.vue";

export default {
  name: "SearchAddFromLibrary",

  data() {
    return {
      resultsPerPage: 10,
      defaultQueries: {
        sort: "name",
        "page[limit]": 10,
      },
      jsonApiFilterManager: JsonApiFilterManagerFactory(),
      selectedBioMaterial: "",
      isBusy: false,
      displayData: {},
      bioMaterials: [],
      fields: [],
      skippedFields: ["name"],
      response: null,
      fieldData: null,
      currentPage: 1,
      totalMaterials: 0,
      copiedBioMaterialUuid: "",
      raftHelper: raftGenericHelper,
      searchAddHovertip: {
        id: "searchAddHt",
        title: "Search",
        text: "Please search by full (scientific) name of material.",
      },
      stepperLabels: ["Search", "Add", "Modify", "Summary"],
    };
  },
  components: {
    BioMaterialRender,
    ShortTextFilter,
    RaftBusy,
    VButton,
  },
  props: {
    materialType: { type: String, required: true },
    groupNid: { type: String, required: true },
    materialTypeLabel: { type: String, required: true },
  },
  async mounted() {
    await this.fetchInitialData();
    this.$emit("change-title", `Add ${this.materialTypeLabel} By Name`);
  },
  methods: {
    cancelSearchAdd() {
      this.$emit("cancel-search-add");
    },
    addToLibrarySelected() {
      this.$emit("material-added", {
        selectedBioMaterial: this.selectedBioMaterial.id,
        selectedBioMaterialData: this.selectedBioMaterial,
        groupQueries: this.groupQueries,
        edit: false,
      });
    },
    errorEmit(message, data) {
      this.$emit("form-error-message", { message: message, data: data });
    },
    async updateLibraryMaterials() {
      this.isBusy = true;
      if (this.currentPage > 1) {
        this.jsonApiFilterManager.setPageOffset(
          (this.currentPage - 1) * this.resultsPerPage,
        );
      } else {
        this.jsonApiFilterManager.queries["page[offset]"] = 0;
      }
      this.response = await jsonClient
        .get({
          path: this.jsonApiPath,
          queries: {
            ...this.groupQueries,
            ...this.jsonApiFilterManager.queries,
          },
        })
        .catch(({ message, data }) => {
          this.$showNotification(
            "error",
            "Unable to update library materials. Please try again.",
          );
          this.errorEmit(message, data);
        });
      this.totalMaterials = 0;
      this.bioMaterials = [];
      if (this.response?.deserialized) {
        this.totalMaterials = parseInt(this.response?.meta?.count, 10);
        const materials = this.response.deserialized;
        for (let i = 0, len = materials.length; i < len; i++) {
          this.bioMaterials.push(materials[i]);
        }
      }
      this.isBusy = false;
    },
    updateCurrentPage(newPage) {
      this.currentPage = newPage;
      this.updateLibraryMaterials();
    },
    async fetchInitialData() {
      const path = "/entity-view-display/bio_material/" + this.materialType;
      const queries = { display_id: ["search_result"] };
      this.jsonApiFilterManager.queries = this.defaultQueries;
      this.jsonApiFilterManager.addFilter("in_library", "in_library", 1);
      this.jsonApiFilterManager.addFilter("status", "status", 1);
      this.jsonApiFilterManager.addFilter(
        "operative_status",
        "operative_status",
        1,
      );
      this.jsonApiFilterManager.addFilter(
        "workflow_status",
        "workflow_status",
        1,
      );
      this.jsonApiFilterManager.deleteFiltersByName(["materialName"]);
      this.bioMaterials = [];
      this.totalMaterials = 0;
      this.response = null;
      const displayResponseData = await restClient
        .get({ path, queries })
        .catch(({ message, data }) => {
          this.isBusy = false;
          this.errorEmit(message, data);
        });
      this.isBusy = false;
      const data = displayResponseData?.[this.materialType];
      this.displayData = data?.search_result;
      if (this.displayData?.field_biomat_plasmid_vector_type) {
        this.jsonApiFilterManager.queries.include =
          "field_biomat_plasmid_vector_type";
      }
      this.setFocusOnInput();
    },
    updateSearch(value) {
      this.updateValue(value, this);
    },
    updateValue: debounce((value, ctx) => {
      if (value.length) {
        const words = value.split(/\s+/);
        let lengthCheck = false;
        words.forEach((item) => {
          if (item.length >= 2) {
            lengthCheck = true;
          }
        });
        if (lengthCheck) {
          ctx.jsonApiFilterManager.addMultiValueContainsAndFilter(
            "materialName",
            "name",
            words,
          );
          ctx.updateLibraryMaterials();
        }
      }
    }, 300),
    getEditLink(materialUuid) {
      let searchAdd = "";
      if (
        window.location.pathname.startsWith("/node/") &&
        !window.location.search.includes("searchAdd")
      ) {
        searchAdd = encodeURIComponent("?searchAdd=1");
      }
      return (
        "/node/" +
        this.groupNid +
        "/biologicals/bio/" +
        this.materialType +
        "/" +
        materialUuid +
        "/edit?copyFromLibrary=1&" +
        raftGenericHelper.getDestinationQuery(false) +
        searchAdd
      );
    },
    async addMaterialStub() {
      this.isBusy = true;
      const path = `/${this.groupNid}/bio-material/create/${this.materialType}`;
      const queries = this.groupQueries;
      const addResponseData = await restClient
        .post({ path, queries, data: {} })
        .catch(({ message, data }) => {
          this.errorEmit(message, data);
        });
      if (addResponseData?.uuid) {
        window.location.href = this.getEditLink(
          addResponseData?.uuid?.[0]?.value,
        );
      }
    },
    setFocusOnInput() {
      setTimeout(() => {
        const input = document.getElementById(
          "bio-lib-listing-" + this.materialType + "-filter",
        );
        if (input) {
          input.focus();
        }
      }, 100);
    },
    backToMaterialTypeSelect() {
      this.$emit("back");
    },
  },
  computed: {
    jsonApiPath() {
      return "/bio_material/" + this.materialType;
    },
    groupQueries() {
      return {
        "rog-id": this.groupNid,
        "rog-type": "node",
      };
    },
    getId() {
      return "bio-lib-listing-" + this.materialType;
    },
    isReady() {
      return (
        raftGenericHelper.isNotEmpty(this.displayData) &&
        raftGenericHelper.isNotEmpty(this.response) &&
        this.totalMaterials > 0
      );
    },
    buttonStyle() {
      return this.selectedBioMaterial ? "primary" : "disabled";
    },
    lowerCasedLabel() {
      return this.materialTypeLabel.toLowerCase();
    },
    showBackButton() {
      const searchParam = window.location.search;
      return searchParam.includes("modal=");
    },
  },
};
</script>

<style lang="css" scoped src="../../css/B3Pagination.css"></style>

<style scoped>
:deep(.flex-row) {
  display: flex;
  justify-content: space-between;
}
.raft-bio-mat-search-result-add-new {
  display: flex;
  gap: 0.5rem;
}
.raft-bio-mat-footer-left {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}
.raft-bio-mat-search-result {
  width: 100%;
}
.raft-bio-mat-search-results-wrapper {
  width: auto;
  height: auto;
  margin: 0 auto;
  padding: 0.6rem;
  position: relative;
}
.raft-bio-mat-search-result {
  display: flex;
  flex-direction: row;
  align-items: center;
  min-height: 3em;
  padding: 0.4em 0;
}
.raft-bio-mat-odd {
  background-color: var(--gray-200);
}
.raft-bio-mat-search-result-actions {
  display: flex;
  padding: 0.5rem 0;
  align-items: center;
  justify-content: space-between;
}
.raft-bio-mat-search-result-actions a {
  font-size: var(--font-size-50);
}
.raft-bio-mat-search-result-data {
  display: inline-block;
  max-width: 95%;
}
.raft-bio-mat-radio {
  padding: 0 0.2rem;
}
.raft-js-search-add-label {
  padding: 0.5rem 0.25rem;
}
.search-add-stepper {
  padding: 1rem 0;
}
.heavy-last-grid-item {
  display: grid;
  grid-template-rows: max-content 1fr max-content;
}
.capped-search-results-height {
  height: calc(0.6rem + 11.05rem * 2.5);
  overflow-y: scroll;
}
</style>
